import React from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router';
import type { RouteObject } from 'react-router';
import { IS_BROWSER } from '@reverbdotcom/env';

import ErrorBoundary from '@reverbdotcom/commons/src/components/error_boundary';
import ErrorDisplay from '../components/discovery/error_display';
import { BROKEN_GUITAR_FATAL_ERROR_NAME } from './route_error_display';

const OFFSET = -60;

export type ReverbBrowserRouter = ReturnType<typeof createBrowserRouter>;

function afterRender(cb: () => void) {
  setTimeout(() => {
    // Opera Mini :( -- how does anything work for them?
    if (!window.requestAnimationFrame) {
      cb();
      return;
    }
    // Wait two animation frames to help ensure that React
    // has flushed some of the elements to the DOM
    requestAnimationFrame(() => {
      requestAnimationFrame(cb);
    });
  }, 0);
}

// Smooth client side routing for anchor tags, find the specified element
// and scroll the window to the element with a offset after the route
// has been navigated
function scrollToAnchor() {
  afterRender(() => {
    const { hash } = window.location;
    if (hash !== '') {
      const id = hash.replace('#', '');
      const element = document.getElementById(id);

      if (element) {
        const top = element.getBoundingClientRect().top + window.pageYOffset + OFFSET;
        window.scrollTo({
          top,
          behavior: 'smooth',
        });
      }
    }
  });
}

export function createReverbBrowserRouter(
  routes: RouteObject[],
  { onUpdate = () => {} } = {},
): ReverbBrowserRouter {
  const router = createBrowserRouter(routes);

  let prevLocation = router.state.location;
  router.subscribe((state) => {
    if (state.navigation.state === 'idle' && state.location !== prevLocation) {
      onUpdate();
      scrollToAnchor();
      prevLocation = state.location;
    }
  });

  return router;
}

export function ReverbRouterProvider({ router }: { router: ReverbBrowserRouter }) {
  return (
    <ErrorBoundary
      errorName={BROKEN_GUITAR_FATAL_ERROR_NAME}
      fallback={<ErrorDisplay />}
    >
      <RouterProvider router={router} />
    </ErrorBoundary>
  );
}

/**
 * Resolves once a client router has loaded the lazy modules for the initial
 * match. If an attempt to hydrate an SSR'd router component is made before
 * the router is initialized, client side React component initialization does not occur.
 */
export async function clientRouterInitialized(router: ReverbBrowserRouter | null): Promise<void> {
  if (!router || router.state.initialized) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const unsub = router.subscribe((state) => {
      if (state.initialized) {
        unsub();
        resolve();
      }
    });
  });
}

let clientRouter: ReverbBrowserRouter | null = null;

/**
 * The current client router, or null if it has not been built (node, or
 * before buildReverbRouter has run). Prefer this over reaching for a global.
 */
export function getClientRouter(): ReverbBrowserRouter | null {
  return clientRouter;
}

function setClientRouter(router: ReverbBrowserRouter | null) {
  clientRouter = router;
}

/**
 * Builds the entry component for routed pages in the browser.
 * The SSR entry component for routed pages is in render_routed_component.tsx.
 *
 * 1. Creates the browser router outside the React tree, per RouterProvider's
 *    contract: "The router prop should be a single router instance created
 *    outside of the React tree."
 *    (https://reactrouter.com/7.18.1/api/data-routers/RouterProvider)
 *    The instance is also held in module state so non-React code can reach it
 *    via getClientRouter. react_renderer.ts awaits its initialization before
 *    hydrating SSR'd HTML for routed pages.
 *
 * 2. Returns a component wrapping RouterProvider. Callers assign it to
 *    window.Reverb.Router (reverb-ui discovery.js, and again in admin.js with
 *    admin routes, last one that loads wins). The name Reverb.Router is a contract:
 *    Core emits data-react-class="Reverb.Router", and react_renderer
 *    awaits initialization only when Reverb.Router is being renderered.
 *    The component owns all routing, for the routed region of the page.
 */
export default function buildReverbRouter(
  routes: RouteObject[],
  { onUpdate = () => {} } = {},
): React.ComponentType {
  // IS_BROWSER is used instead of `typeof document !== 'undefined'` because this module
  // is part of the SSR bundle loaded by RRE and executed in a jsdom environment, where document _is_ defined.
  if (!IS_BROWSER) {
    return function ReverbRouter() {
      return null;
    };
  }

  const router = createReverbBrowserRouter(routes, { onUpdate });
  setClientRouter(router);

  return function ReverbRouter() {
    return <ReverbRouterProvider router={router} />;
  };
}
